import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { API_BASE_URL, API_KEY, getApiHeaders } from '../config/api';

const walletApiBase = `${API_BASE_URL.replace(/\/$/, '')}/wallet/t`;

const loadLottie = (): Promise<any> =>
  new Promise((resolve) => {
    const win = window as any;
    if (win.lottie) return resolve(win.lottie);
    const existing = document.querySelector('script[data-lottie]');
    if (existing) {
      const interval = setInterval(() => {
        if (win.lottie) { clearInterval(interval); resolve(win.lottie); }
      }, 100);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.12.2/lottie.min.js';
    script.setAttribute('data-lottie', '1');
    script.onload = () => resolve(win.lottie);
    document.head.appendChild(script);
  });

const LottiePreview: React.FC<{ data: any }> = ({ data }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<any>(null);

  useEffect(() => {
    let cancelled = false;
    loadLottie().then((lottie) => {
      if (cancelled || !containerRef.current) return;
      animRef.current?.destroy();
      animRef.current = lottie.loadAnimation({
        container: containerRef.current,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData: data,
      });
    });
    return () => {
      cancelled = true;
      animRef.current?.destroy();
    };
  }, [data]);

  return <div ref={containerRef} style={{ width: 160, height: 160 }} />;
};

const CreateGiftAnimation: React.FC = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<any>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const headerWithAuth = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (!token) { toast.error('No token found. Please log in.'); return null; }
    if (!API_KEY) { toast.error('Missing API key.'); return null; }
    return getApiHeaders(token);
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] || null;
    setFile(selected);
    setPreviewData(null);
    if (!selected) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const json = JSON.parse(evt.target?.result as string);
        setPreviewData(json);
      } catch {
        toast.error('Invalid JSON file — could not parse for preview.');
      }
    };
    reader.readAsText(selected);
  };

  const handleUpload = async () => {
    if (!name.trim()) { toast.error('Name is required.'); return; }
    if (!file) { toast.error('Select a JSON animation file.'); return; }
    const headers = headerWithAuth();
    if (!headers) return;

    setUploading(true);
    try {
      // Step 1: Get presigned URL
      const presignRes = await fetch(`${walletApiBase}/presignGiftAnimationUpload`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ Name: name.trim(), ContentType: 'application/json' }),
      });
      const presignJson = await presignRes.json();
      if (!presignRes.ok) throw new Error(presignJson.message || 'Failed to get upload URL');
      const { AnimationKey, FileKey, FileUrl, UploadUrl } = presignJson.data;

      // Step 2: Upload to R2
      const fileBuffer = await file.arrayBuffer();
      const uploadRes = await fetch(UploadUrl, { method: 'PUT', body: fileBuffer });
      if (!uploadRes.ok) throw new Error('Failed to upload file to storage');

      // Step 3: Save record
      setSaving(true);
      const saveRes = await fetch(`${walletApiBase}/saveGiftAnimation`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          AnimationKey,
          Name: name.trim(),
          Description: description.trim() || undefined,
          FileKey,
          FileUrl,
        }),
      });
      const saveJson = await saveRes.json();
      if (!saveRes.ok) throw new Error(saveJson.message || 'Failed to save animation');

      toast.success('Gift animation uploaded successfully');
      navigate('/gift-animations');
    } catch (error) {
      toast.error((error as Error).message || 'Upload failed');
    } finally {
      setUploading(false);
      setSaving(false);
    }
  };

  const isWorking = uploading || saving;

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />
      <div className='max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10'>
        <div className='flex items-center justify-between mb-8'>
          <div>
            <h1 className='text-2xl sm:text-3xl font-bold text-white'>Upload Gift Animation</h1>
            <p className='text-sm text-gray-400'>Lottie JSON animation for token gifts.</p>
          </div>
          <button
            onClick={() => navigate('/gift-animations')}
            className='px-4 py-2 text-sm font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20'
          >
            Back
          </button>
        </div>

        <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 space-y-5'>
          <div>
            <label className='block text-xs text-gray-400 mb-1'>Name *</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='e.g. Dragon Fire'
              className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-600'
            />
          </div>
          <div>
            <label className='block text-xs text-gray-400 mb-1'>Description</label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder='Optional description'
              className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-600'
            />
          </div>
          <div>
            <label className='block text-xs text-gray-400 mb-1'>Animation File (.json) *</label>
            <input
              type='file'
              accept='application/json,.json'
              onChange={handleFileChange}
              className='w-full text-sm text-gray-300'
            />
            <p className='text-[11px] text-gray-500 mt-1'>Lottie JSON format. Will be uploaded to CDN.</p>
          </div>

          {previewData && (
            <div className='bg-black/20 border border-white/10 rounded-xl p-4 flex flex-col items-center gap-3'>
              <p className='text-xs text-gray-400 self-start'>Animation Preview</p>
              <LottiePreview data={previewData} />
              <p className='text-xs text-emerald-400'>Preview loaded successfully</p>
            </div>
          )}

          <button
            onClick={handleUpload}
            disabled={isWorking || !file || !name.trim()}
            className='w-full py-3 bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white font-semibold rounded-lg disabled:opacity-60 transition'
          >
            {uploading ? 'Uploading to R2...' : saving ? 'Saving...' : 'Upload Animation'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateGiftAnimation;
